import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  DashboardOutlined,
  InventoryOutlined,
  AnalyticsOutlined,
  RateReviewOutlined,
  BusinessOutlined,
  AssessmentOutlined,
  SettingsOutlined,
  SecurityOutlined,
  TrendingUpOutlined,
  WarningOutlined,
  AutoFixHighOutlined,
  InsightsOutlined,
  SpeedOutlined,
  VerifiedOutlined,
  BatchPredictionOutlined,
} from '@mui/icons-material';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const menuItems = [
  {
    text: 'Dashboard',
    description: 'Overview & insights',
    icon: <DashboardOutlined />,
    path: '/dashboard',
  },
  {
    text: 'Products',
    description: 'Manage product catalog',
    icon: <InventoryOutlined />,
    path: '/products',
  },
  {
    text: 'Analyses',
    description: 'View analysis results',
    icon: <AnalyticsOutlined />,
    path: '/analyses',
  },
  {
    text: 'Bulk Analysis',
    description: 'Batch counterfeit detection',
    icon: <BatchPredictionOutlined />,
    path: '/enhanced-analysis',
  },
  {
    text: 'Analytics',
    description: 'Performance metrics',
    icon: <InsightsOutlined />,
    path: '/analytics',
  },
  {
    text: 'Review Queue',
    description: 'Pending reviews',
    icon: <RateReviewOutlined />,
    path: '/review-queue',
  },
  {
    text: 'Brands',
    description: 'Brand management',
    icon: <BusinessOutlined />,
    path: '/brands',
  },
  {
    text: 'Reports',
    description: 'Generate reports',
    icon: <AssessmentOutlined />,
    path: '/reports',
  },
];

const adminMenuItems = [
  {
    text: 'Settings',
    description: 'System configuration',
    icon: <SettingsOutlined />,
    path: '/settings',
  },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const drawerWidth = 280;

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          mt: 8, // Account for AppBar height
          borderRight: '1px solid #e2e8f0',
        },
      }}
    >
      <Box sx={{ overflow: 'auto', py: 1 }}>
        {/* Main Menu */}
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(37, 99, 235, 0.12)',
                    color: '#2563eb',
                    '&:hover': {
                      backgroundColor: 'rgba(37, 99, 235, 0.16)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#2563eb',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#2563eb' : '#64748b',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 500,
                      fontSize: '0.875rem',
                    }}
                    secondary={item.description}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: location.pathname === item.path ? 'rgba(37, 99, 235, 0.7)' : '#94a3b8',
                    }}
                  />
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2, mx: 2 }} />

        {/* Admin Menu */}
        <List>
          <ListItem sx={{ px: 3, py: 1 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
              }}
            >
              Administration
            </Typography>
          </ListItem>
          {adminMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(37, 99, 235, 0.12)',
                    color: '#2563eb',
                    '&:hover': {
                      backgroundColor: 'rgba(37, 99, 235, 0.16)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#2563eb',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#2563eb' : '#64748b',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 500,
                      fontSize: '0.875rem',
                    }}
                    secondary={item.description}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: location.pathname === item.path ? 'rgba(37, 99, 235, 0.7)' : '#94a3b8',
                    }}
                  />
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2, mx: 2 }} />

        {/* Quick Stats */}
        <Box sx={{ p: 3 }}>
          <Typography 
            variant="overline" 
            sx={{ 
              color: '#64748b',
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              mb: 2,
              display: 'block',
            }}
          >
            System Stats
          </Typography>
          
          <Box sx={{ space: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(37, 99, 235, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(37, 99, 235, 0.1)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SecurityOutlined sx={{ fontSize: 18, mr: 1.5, color: '#2563eb' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    Total Analyses
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    1,234
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="+12%" 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              p: 1.5,
              backgroundColor: 'rgba(239, 68, 68, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(239, 68, 68, 0.1)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningOutlined sx={{ fontSize: 18, mr: 1.5, color: '#ef4444' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    Counterfeits Found
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    89
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="-5%" 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 1.5,
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(16, 185, 129, 0.1)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <VerifiedOutlined sx={{ fontSize: 18, mr: 1.5, color: '#10b981' }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    Accuracy Rate
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    95.2%
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="+2.1%" 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 